import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send, Mail, MapPin, Phone } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const EASE = [0.22, 1, 0.36, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error(t.contact.error);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, lang });
      toast.success(t.contact.success);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(t.contact.error);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-mobix-mauve/[0.07] border border-transparent rounded-3xl px-6 py-4 text-mobix-navy placeholder:text-mobix-navy/40 outline-none focus:border-mobix-orange/50 focus:bg-white transition-colors duration-300";

  return (
    <section id="contact" className="relative bg-[#F9FAFB] py-28 lg:py-40" data-testid="contact-section">
      <div className="max-w-[1800px] mx-auto px-6 md:px-12 lg:px-20 flex flex-col gap-12 lg:grid lg:grid-cols-12 lg:gap-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="col-span-12 lg:col-span-5"
        >
          <p className="text-[13px] font-bold tracking-[0.24em] uppercase text-mobix-orange mb-6">{t.contact.overline}</p>
          <h2 className="font-extrabold uppercase tracking-tight text-mobix-navy text-[clamp(1.875rem,4.8vw,3.75rem)] leading-[1.06] break-words" data-testid="contact-title">
            {t.contact.title}<span className="text-mobix-orange">.</span>
          </h2>
          <span className="mt-8 block h-[3px] w-32 bg-mobix-orange" aria-hidden="true" />
          <p className="mt-8 text-base sm:text-lg font-medium text-mobix-navy/70 leading-relaxed max-w-md">{t.contact.subtitle}</p>

          <div className="mt-12 space-y-7">
            <div className="flex items-start gap-5">
              <span className="h-12 w-12 rounded-full bg-mobix-mauve/15 flex items-center justify-center shrink-0"><MapPin size={20} className="text-mobix-orange" /></span>
              <div>
                <p className="text-xs font-bold tracking-[0.16em] uppercase text-mobix-orange mb-1.5">{t.contact.addressLabel}</p>
                <p className="text-[15px] font-medium text-mobix-navy max-w-[16rem]">{t.contact.address}</p>
              </div>
            </div>
            <div className="flex items-start gap-5">
              <span className="h-12 w-12 rounded-full bg-mobix-mauve/15 flex items-center justify-center shrink-0"><Mail size={20} className="text-mobix-orange" /></span>
              <div>
                <p className="text-xs font-bold tracking-[0.16em] uppercase text-mobix-orange mb-1.5">{t.contact.emailLabel}</p>
                <a href={`mailto:${t.contact.emailValue}`} data-testid="contact-email-link" className="text-[15px] font-medium text-mobix-navy underline underline-offset-4 decoration-mobix-navy/30 hover:decoration-mobix-orange transition-colors">{t.contact.emailValue}</a>
              </div>
            </div>
            <div className="flex items-start gap-5">
              <span className="h-12 w-12 rounded-full bg-mobix-mauve/15 flex items-center justify-center shrink-0"><Phone size={20} className="text-mobix-orange" /></span>
              <div>
                <p className="text-xs font-bold tracking-[0.16em] uppercase text-mobix-orange mb-1.5">{t.contact.phoneLabel}</p>
                <a href={`tel:${t.contact.phoneValue.replace(/\s/g, "")}`} data-testid="contact-phone-link" className="text-[15px] font-medium text-mobix-navy hover:text-mobix-orange transition-colors">{t.contact.phoneValue}</a>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.12 }}
          className="col-span-12 lg:col-span-7 w-full max-w-full rounded-3xl bg-white border border-mobix-mauve/15 p-6 sm:p-10 lg:p-12 shadow-[0_20px_60px_rgba(11,43,86,0.06)]"
          data-testid="contact-form"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <input data-testid="contact-name" className={inputCls} placeholder={t.contact.name} value={form.name} onChange={update("name")} />
            <input data-testid="contact-phone" className={inputCls} placeholder={t.contact.phone} value={form.phone} onChange={update("phone")} />
          </div>
          <input data-testid="contact-email" type="email" className={`${inputCls} mt-5`} placeholder={t.contact.email} value={form.email} onChange={update("email")} />
          <textarea data-testid="contact-message" rows={5} className={`${inputCls} mt-5 resize-none`} placeholder={t.contact.message} value={form.message} onChange={update("message")} />
          <button
            type="submit"
            disabled={loading}
            data-testid="contact-submit"
            className="mt-7 group inline-flex items-center gap-2 px-9 py-4 rounded-3xl bg-mobix-orange text-white font-semibold shadow-lg shadow-mobix-orange/30 hover:scale-105 transition-transform duration-300 disabled:opacity-60 disabled:hover:scale-100"
          >
            {loading ? t.contact.sending : t.contact.submit}
            {!loading && <Send size={17} className="group-hover:translate-x-0.5 transition-transform duration-300" />}
          </button>
          <p className="mt-6 text-xs text-mobix-navy/45 leading-relaxed max-w-lg" data-testid="contact-disclaimer">
            {t.contact.disclaimer}
          </p>
        </motion.form>
      </div>
    </section>
  );
}
